import { v4 as uuidv4 } from 'uuid';

import type {
  ButtonElement,
  ImageElement,
  LineElement,
  RectElement,
  TextElement,
} from 'Components/CanvasEditor/types';
import type { AnyObject } from 'types';

export function getImageElementProps({
  width,
  height,
  url,
  isAddImage = false,
}: {
  width: number;
  height: number;
  url: string;
  isAddImage?: boolean;
}): Promise<ImageElement> {
  return new Promise<ImageElement>(resolve => {
    const img = new Image();
    img.src = url;
    img.onload = () => {
      URL.revokeObjectURL(url);
      const originalImageAspectRatio = img.width / img.height;
      const aspectRatio = width / height;

      let cropWidth: number;
      let cropHeight: number;

      if (aspectRatio >= originalImageAspectRatio) {
        cropWidth = img.width;
        cropHeight = img.width / aspectRatio;
      } else {
        cropWidth = img.height * aspectRatio;
        cropHeight = img.height;
      }

      const cropX = (img.width - cropWidth) / 2;
      const cropY = (img.height - cropHeight) / 2;

      if (isAddImage) {
        const stageWidth = width;
        const stageHeight = height;

        const stageAspectRatio = stageWidth / stageHeight;

        if (originalImageAspectRatio > 1) {
          let newWidth = stageWidth;
          let newHeight = newWidth / originalImageAspectRatio;
          let x = 0;
          let y = (stageHeight - newHeight) / 2;

          if (stageAspectRatio > originalImageAspectRatio) {
            newHeight = stageHeight;
            newWidth = newHeight * originalImageAspectRatio;

            x = (stageWidth - newWidth) / 2;
            y = 0;
          }

          resolve({
            id: uuidv4(),
            type: 'image',
            src: url,
            visible: true,
            rotation: 0,
            draggable: true,
            x,
            y,
            width: newWidth,
            height: newHeight,
            cropWidth,
            cropHeight,
            cropX,
            cropY,
            mask: 'rect',
          });
        }

        if (originalImageAspectRatio < 1) {
          let newHeight = stageHeight;
          let newWidth = newHeight * originalImageAspectRatio;
          let x = (stageWidth - newWidth) / 2;
          let y = 0;

          if (stageAspectRatio < originalImageAspectRatio) {
            newWidth = stageWidth;
            newHeight = newWidth / originalImageAspectRatio;

            x = 0;
            y = (stageHeight - newHeight) / 2;
          }

          resolve({
            id: uuidv4(),
            type: 'image',
            src: url,
            visible: true,
            rotation: 0,
            draggable: true,
            x,
            y,
            width: newWidth,
            height: newHeight,
            cropWidth,
            cropHeight,
            cropX,
            cropY,
            mask: 'rect',
          });
        }

        if (originalImageAspectRatio === 1) {
          let width = stageWidth;
          let height = stageHeight;

          if (stageWidth > stageHeight) {
            width = height * originalImageAspectRatio;

            resolve({
              id: uuidv4(),
              type: 'image',
              src: url,
              visible: true,
              rotation: 0,
              draggable: true,
              x: (stageWidth - width) / 2,
              y: 0,
              width,
              height,
              cropWidth,
              cropHeight,
              cropX,
              cropY,
              mask: 'rect',
            });
          } else {
            height = width * originalImageAspectRatio;

            resolve({
              id: uuidv4(),
              type: 'image',
              src: url,
              visible: true,
              rotation: 0,
              draggable: true,
              x: 0,
              y: (stageHeight - height) / 2,
              width,
              height,
              cropWidth,
              cropHeight,
              cropX,
              cropY,
              mask: 'rect',
            });
          }
        }
      } else {
        resolve({
          id: uuidv4(),
          type: 'image',
          src: url,
          visible: true,
          rotation: 0,
          draggable: true,
          x: 0,
          y: 0,
          width,
          height,
          cropWidth,
          cropHeight,
          cropX,
          cropY,
          mask: 'rect',
        });
      }
    };
  });
}

// eslint-disable-next-line consistent-return
export async function parseElements(data: AnyObject[]) {
  const elements = await Promise.all(
    data
      .filter(item => item.role !== 'button_text')
      .map(async item => {
        const base = {
          id: uuidv4(),
          x: item.x ?? item.left ?? 0,
          y: item.y ?? item.top ?? 0,
          width: item.width ?? null,
          height: item.height ?? null,
          rotation: 0,
          draggable: true,
          visible: true,
        };

        if (item.type === 'image') {
          const image: ImageElement = await getImageElementProps({
            width: item.width,
            height: item.height,
            url: item.src,
          });

          if (item.crop_to_cycle) {
            image.mask = 'circle';
          }

          return { ...image, ...base };
        }
        if (item.type === 'text' && item.role !== 'button_text') {
          let fontFamily = 'Arial';
          if (item.font.includes('OpenSans')) fontFamily = 'Open Sans';
          if (item.font.includes('Roboto')) fontFamily = 'Roboto';

          const text: TextElement = {
            ...base,
            type: 'text',
            text: item.text,
            fontSize: item.fontSize,
            fontFamily,
            fill: item.fill || '#000000',
            align: 'center',
            scaleX: 1,
            scaleY: 1,
          };
          return text;
        }
        if (item.type === 'polygon') {
          const flattenedPoints = item.points.flat(); // convert [[x,y], [x,y], ...] to [x, y, x, y, ...]
          const line: LineElement = {
            ...base,
            type: 'line',
            closed: true,
            points: flattenedPoints,
            fill: item.fill || '#000000',
            stroke: item.fill || '#000000',
            strokeWidth: 0,
          };

          return line;
        }
        if (item.type === 'line') {
          const points = [
            item.start_position.left ?? 0,
            item.start_position.top ?? 0,
            item.end_position.right ?? 0,
            item.end_position.bottom ?? 0,
          ];
          const line: LineElement = {
            ...base,
            type: 'line',
            points,
            closed: false,
            fill: item.fill || '#000000',
            stroke: item.fill || '#000000',
            strokeWidth: item.width || 1,
          };
          return line;
        }
        if (item.type === 'rect') {
          const rect: RectElement = {
            ...base,
            type: 'rect',
            fill: item.fill || '#000000',
            stroke: item.fill || '#000000',
            strokeWidth: 0,
          };

          return rect;
        }
        if (item.type === 'rounded-rect') {
          if (item.role === 'button') {
            const itemText = data.find(item => item.role === 'button_text');
            let fontFamily = 'Arial';
            if (itemText?.font.includes('OpenSans')) fontFamily = 'Open Sans';
            if (itemText?.font.includes('Roboto')) fontFamily = 'Roboto';

            const button: ButtonElement = {
              ...base,
              type: 'text',
              text: itemText?.text,
              fontSize: itemText?.fontSize,
              fontFamily,
              fill: itemText?.fill || '#000000',
              align: 'center',
              background: item.fill || '#000000',
              cornerRadius: item.radius,
              scaleX: 1,
              scaleY: 1,
            };

            return button;
          }
          const includeAllCorners = (item.corners as boolean[]).every(corner => corner);
          const rect: RectElement = {
            ...base,
            type: 'rect',
            fill: item.fill || '#000000',
            stroke: item.fill || '#000000',
            strokeWidth: 0,
            cornerRadius: includeAllCorners
              ? item.radius
              : (item.corners as boolean[]).map(corner => (corner ? item.radius : 0)),
          };

          return rect;
        }
        return item;
      })
  );

  return elements;
}
